import React from 'react'
import {Tooltip} from 'antd'
import {sparkSegmentsOf, trendDirectionOf} from '../../helpers/efficiencyReport'
import {DASH, formatDecimal} from '../../helpers/format'

/*
 * ⚠️ File riêng vì có HAI chỗ dùng: cột "Xu hướng" của bảng (84×22, một instance mỗi dòng) và thẻ
 *    KPI Hiệu suất (rộng hơn, một instance mỗi trang, FR-006). Để nó nằm trong `efficiencyColumns`
 *    thì trang phải import một module tên là "cột" chỉ để lấy một hình vẽ.
 */

/**
 * Đường hiệu suất theo ngày, vẽ gọn trong một ô bảng.
 *
 * SVG viết tay chứ không kéo thư viện biểu đồ về: cả cột chỉ cần một polyline, còn mỗi dòng bảng
 * dựng một instance của thư viện là vài trăm instance trên một màn hình cuộn.
 *
 * ⚠️ Màu KHÔNG phải kênh thông tin (AC-UI-01): đường vẽ bằng màu chữ phụ, còn hướng lên/xuống được
 *    nói bằng CHỮ trong tooltip và trong `aria-label`. Người đọc màn hình và người ngồi trước màn
 *    hình xưởng độ tương phản thấp đều phải nhận được cùng một thông tin.
 */
const Sparkline = ({trend, width = SPARK_W, height = SPARK_H}) => {
    const {segments, dots, last} = sparkSegmentsOf(trend, {width, height})
    const direction = trendDirectionOf(trend)

    if (segments.length === 0 && dots.length === 0) {
        return (
            <Tooltip title="Chưa ngày nào trong khoảng này tính được hiệu suất để vẽ xu hướng">
                <span className="VcZero VcHasTooltip">{DASH}</span>
            </Tooltip>
        )
    }

    const word = direction && DIRECTION_WORDS[direction.direction]
    /*
     * Nói luôn MẬT ĐỘ dữ liệu trong tooltip. Đường gãy khúc gần như luôn là vì thiếu ngày chứ
     * không vì người đó làm thất thường — không nói ra thì người đọc quy cho con người.
     */
    /*
     * ⚠️ Nói "tính được hiệu suất", KHÔNG nói "đã nhập giờ". Service trả `efficiency: null` vì BA
     *    lý do: chưa nhập giờ · đã khai NGHỈ (`hours <= 0`, mã `NL`/`P`) · đã nhập giờ nhưng hôm
     *    đó không có sản lượng nào quy được về người. Ghi "chưa nhập giờ" là cử quản lý mở màn Giờ
     *    làm thực tế đi tìm những ngày đã có giờ đầy đủ.
     */
    const measured = (trend || []).filter((p) => p && typeof p.efficiency === 'number').length
    const density = `${measured}/${(trend || []).length} ngày tính được hiệu suất`
    const label = word
        ? `Xu hướng hiệu suất theo ngày: ${word}, ${formatDecimal(Math.abs(direction.delta) * 100, 1)} điểm · ${density}`
        : `Xu hướng hiệu suất theo ngày · ${density}`

    return (
        <Tooltip title={label}>
            <svg
                className="VcSpark"
                width={width}
                height={height}
                viewBox={`0 0 ${width} ${height}`}
                role="img"
                aria-label={label}
            >
                {segments.map((seg) => (
                    <polyline key={seg[0].day} points={seg.map((q) => `${q.x},${q.y}`).join(' ')}/>
                ))}
                {/*
                  * Ngày đo được nhưng hai ngày kề đều trống — vẽ CHẤM chứ không bỏ. Một đường
                  * không nối tới nó được không có nghĩa là nó không tồn tại.
                  */}
                {dots.map((q) => (
                    <circle key={q.day} className="VcSpark__dot" cx={q.x} cy={q.y} r="1.3"/>
                ))}
                {last ? <circle cx={last.x} cy={last.y} r="1.8"/> : null}
            </svg>
        </Tooltip>
    )
}

const SPARK_W = 84
const SPARK_H = 22
const DIRECTION_WORDS = {up: 'đang tăng', down: 'đang giảm', flat: 'đi ngang'}

export default Sparkline
